import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";

import {
  approveResponseSchema,
  decidedListResponseSchema,
  pendingListResponseSchema,
  type ApproveInput,
  type ApproveResponse,
  type DecidedListResponse,
  type DenyInput,
  type PendingListResponse,
} from "./schemas";

const baseFor = (tenantId: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantId)}/admin-recovery`;

export async function fetchPendingRecoveryRequests(
  tenantId: string,
): Promise<PendingListResponse> {
  const raw = await apiFetch<unknown>(`${baseFor(tenantId)}/pending`);
  return pendingListResponseSchema.parse(raw);
}

export function usePendingRecoveryRequestsQuery(tenantId: string) {
  return useQuery<PendingListResponse, ApiError>({
    queryKey: queryKeys.adminRecovery.pending(tenantId),
    queryFn: () => fetchPendingRecoveryRequests(tenantId),
    refetchInterval: 30 * 1000, // 30s polling per CLAUDE.md cadence
    enabled: Boolean(tenantId),
  });
}

export async function fetchDecidedRecoveryRequests(
  tenantId: string,
): Promise<DecidedListResponse> {
  const raw = await apiFetch<unknown>(`${baseFor(tenantId)}/decided`);
  return decidedListResponseSchema.parse(raw);
}

export function useAdminRecoveryDecidedQuery(tenantId: string) {
  return useQuery<DecidedListResponse, ApiError>({
    queryKey: queryKeys.adminRecovery.decided(tenantId),
    queryFn: () => fetchDecidedRecoveryRequests(tenantId),
    // Decided rows are immutable history — no aggressive polling.
    staleTime: 30 * 1000,
    enabled: Boolean(tenantId),
  });
}

export function useApproveRecoveryMutation(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<
    ApproveResponse,
    ApiError,
    { requestId: string; input: ApproveInput }
  >({
    mutationFn: async ({ requestId, input }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${baseFor(tenantId)}/${encodeURIComponent(requestId)}/approve`,
          { method: "POST", body: input },
        );
        return approveResponseSchema.parse(raw);
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.adminRecovery.all(tenantId) });
    },
  });
}

export function useDenyRecoveryMutation(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { requestId: string; input: DenyInput }
  >({
    mutationFn: ({ requestId, input }) =>
      withStepUp(() =>
        apiFetch<void>(
          `${baseFor(tenantId)}/${encodeURIComponent(requestId)}/deny`,
          { method: "POST", body: input },
        ),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.adminRecovery.all(tenantId) });
    },
  });
}
